import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    countryCode: '+94',
    email: '',
    solutions: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_name: formData.name,
        company_name: formData.company,
        phone_number: `${formData.countryCode} ${formData.phone}`,
        from_email: formData.email,
        solutions: formData.solutions,
        message_html: formData.solutions +" \n "+formData.message,
        to_name: 'Sujee',
        reply_to: formData.email
      };

      await emailjs.send(
        'squirrel',
        'template_bYKAoUmU',
        templateParams,
        'Qs_Ookf-OfWJ9GXaW'
      );

      setSubmitStatus('success');
      setFormData({
        from_name: '',
        company_name: '',
        phone_number: '',
        from_email: '',
        solutions: '',
        message_html: '',
        to_name: 'Sujee',
        reply_to: formData.email
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold font-manrope mb-6">
              Get In <span className="text-primary">Touch</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
              Let's discuss how we can help you leverage technology and data to transform your business.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-3xl font-bold font-manrope mb-4">Contact Information</h3>
              <p className="text-muted-foreground mb-10">
                Fill out the form and our team will get back to you within 24 hours.
              </p>

              <div className="space-y-8">
                <div className="flex items-center gap-5" data-testid="contact-email">
                  <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-primary font-semibold mb-1">Email Us</h4>
                    <a href="mailto:info@serenditech.com" className="text-muted-foreground hover:text-primary transition-colors">
                      info@serenditech.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-testid="contact-phone">
                  <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-primary font-semibold mb-1">Call Us</h4>
                    <a href="tel:+94762646107" className="text-muted-foreground hover:text-primary transition-colors">
                      +94 76 264 6107
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-testid="contact-address">
                  <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-primary font-semibold mb-1">Visit Us</h4>
                    <p className="text-muted-foreground">
                      396 B Galle Road<br />Colombo 06, Sri Lanka
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-testid="contact-hours">
                  <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-primary font-semibold mb-1">Business Hours</h4>
                    <p className="text-muted-foreground">Mon - Fri: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-card p-8 md:p-10 rounded-2xl border border-border"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold font-manrope mb-8">Send us a Message</h3>

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                      data-testid="form-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name"
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                      data-testid="form-input-company"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="w-32 px-2 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                        data-testid="form-input-country-code"
                      >
                        <option value="+94">🇱🇰 +94</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+974">🇶🇦 +974</option>
                        <option value="+966">🇸🇦 +966</option>
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                      </select>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        required
                        className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                        data-testid="form-input-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@company.com"
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                    data-testid="form-input-email"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="solutions" className="block text-sm font-medium mb-2">
                    Solutions Interested In <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="solutions"
                    name="solutions"
                    value={formData.solutions}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                    data-testid="form-input-solutions"
                  >
                    <option value="">-Select-</option>
                    <option value="Power Platform">Microsoft Power Platform</option>
                    <option value="Power Apps">Power Apps Development</option>
                    <option value="Power Automate">Power Automate & RPA</option>
                    <option value="Power BI">Power BI & Analytics</option>
                    <option value="Copilot Studio">Copilot Studio & AI</option>
                    <option value="Custom Development">Custom Software Development</option>
                    <option value="Web Development">Web Application Development</option>
                    <option value="Mobile Development">Mobile App Development</option>
                    <option value="Azure Cloud">Azure Cloud Development</option>
                    <option value="Data Warehousing">Data Warehousing & Engineering</option>
                    <option value="Business Intelligence">Business Intelligence</option>
                    <option value="Machine Learning">Advanced Analytics & ML</option>
                    <option value="Xero Integration">Xero Implementation & Integration</option>
                    <option value="Multiple Services">Multiple Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or requirements..."
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none"
                    data-testid="form-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-4 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="form-submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-5 p-4 bg-primary/15 border border-primary rounded-lg text-primary" data-testid="form-success-message">
                    Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-5 p-4 bg-red-500/15 border border-red-500 rounded-lg text-red-500" data-testid="form-error-message">
                    Oops! Something went wrong. Please try again or contact us directly at info@serenditech.com
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;